import { useEffect, useRef, useState } from "react";
import { MessageCircleQuestion, X, Send, Loader2, Volume2, Pause, AlertCircle, Lock, RefreshCw } from "lucide-react";
import { http } from "../lib/api";
import { useAuth } from "../auth/AuthContext";

/**
 * Ask the Reporter modal — readers can ask multi-turn questions of the
 * Newseze avatar (Chella / Michael / Marcus) about a specific article.
 *
 * Defaults: avatar auto-assigned by article category, with a "switch reporter"
 * dropdown. TTS is opt-in via "Listen to answer" button per turn.
 */
export default function AskReporterModal({ article, onClose, onUpgrade }) {
  const { token, user } = useAuth();
  const [avatars, setAvatars] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [quota, setQuota] = useState(null);
  const [audioByTurn, setAudioByTurn] = useState({});
  const [loadingAudio, setLoadingAudio] = useState(null);
  const audioRef = useRef(null);
  const transcriptEndRef = useRef(null);

  // ESC closes + body scroll lock (mobile safety net)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Suggested starter questions
  const STARTERS = [
    "Why is this happening now?",
    "Who benefits if this plays out?",
    "What should I watch next?",
  ];

  // Load roster on open
  useEffect(() => {
    http.get("/avatars").then((r) => setAvatars(r.data?.avatars || [])).catch(() => {});
  }, []);

  // Auto-scroll to bottom on new turn
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns.length]);

  const sendQuestion = async (q) => {
    const text = (q || question).trim();
    if (!text) return;
    setBusy(true); setErr("");
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const body = { article_id: article.id, question: text };
      if (sessionId) body.session_id = sessionId;
      if (selectedAvatar) body.avatar_id = selectedAvatar;
      const r = await http.post("/reporter/ask", body, { headers });
      setSessionId(r.data.session_id);
      setQuota(r.data.quota);
      setSelectedAvatar(r.data.avatar.id);
      setTurns((t) => [...t, { question: r.data.question, answer: r.data.answer, avatar: r.data.avatar, idx: r.data.turn_index }]);
      setQuestion("");
    } catch (ex) {
      const detail = ex?.response?.data?.detail || "Could not reach the reporter. Try again.";
      setErr(detail);
      if (ex?.response?.status === 429 && /upgrade/i.test(detail)) {
        // Surface upgrade CTA inline; modal stays open
      }
    } finally {
      setBusy(false);
    }
  };

  const playAnswer = async (idx) => {
    if (!sessionId) return;
    // If we already have this audio cached locally, play it
    if (audioByTurn[idx]) {
      const a = new Audio(audioByTurn[idx]);
      audioRef.current?.pause?.();
      audioRef.current = a;
      a.play();
      return;
    }
    setLoadingAudio(idx);
    try {
      const r = await http.post("/reporter/listen", { article_id: article.id, session_id: sessionId });
      if (r.data?.audio_base64) {
        const url = `data:audio/mpeg;base64,${r.data.audio_base64}`;
        setAudioByTurn((m) => ({ ...m, [idx]: url }));
        const a = new Audio(url);
        audioRef.current?.pause?.();
        audioRef.current = a;
        a.play();
      } else if (r.data?.reason) {
        setErr(
          r.data.reason === "elevenlabs_key_missing"
            ? "Voice playback is being set up. Reading aloud is preparing."
            : r.data.reason === "daily_cap_reached"
              ? "Daily voice cap reached site-wide. Try again tomorrow."
              : "Audio unavailable."
        );
        setTimeout(() => setErr(""), 4000);
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Audio unavailable.");
      setTimeout(() => setErr(""), 4000);
    } finally {
      setLoadingAudio(null);
    }
  };

  const switchReporter = (avid) => {
    setSelectedAvatar(avid);
    // Switching mid-session forks to a new session so Claude's per-session
    // memory doesn't bleed personas. Transcript stays visible to the reader.
    setSessionId(null);
  };

  const activeAvatar = avatars.find((a) => a.id === selectedAvatar) || avatars.find((a) => a.id === turns[0]?.avatar?.id) || null;
  const isFreeTier = !user || (user.tier || "free") === "free";

  return (
    <div
      className="fixed inset-0 bg-black/65 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="ask-reporter-modal"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-[680px] w-full max-h-[92vh] sm:max-h-[88vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 bg-gradient-to-r from-[#0a1f3d] to-[#1e3a8a] text-white p-4 sm:p-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {activeAvatar?.portrait ? (
              <img src={activeAvatar.portrait} alt={activeAvatar.name} className="w-10 h-10 rounded-full bg-white p-0.5 shrink-0" data-testid="ask-reporter-avatar" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[var(--gold)] text-[#1d1303] flex items-center justify-center shrink-0" data-testid="ask-reporter-avatar">
                <MessageCircleQuestion size={18} />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--gold)]">Questeze</div>
              <div className="text-[13.5px] font-bold truncate">
                {activeAvatar?.name ? `${activeAvatar.name} · ${activeAvatar.role}` : "Newseze Newsroom"}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-11 h-11 -mr-2 flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition"
            aria-label="Close"
            data-testid="ask-reporter-close"
          >
            <X size={22} strokeWidth={2.5} />
          </button>
        </div>

        {/* Reporter switcher + article */}
        <div className="shrink-0 border-b border-[var(--line)] p-3 sm:p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--muted)] mb-1.5">About this story</div>
          <div className="text-[13px] font-bold text-[var(--ink)] leading-snug mb-3" data-testid="ask-reporter-story-title">{article.title}</div>
          {avatars.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="ask-reporter-switcher">
              <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">Reporter:</span>
              {avatars.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => switchReporter(a.id)}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-black transition ${(activeAvatar?.id || "") === a.id ? "bg-[var(--ink)] text-white" : "bg-[var(--bg)] text-[var(--muted)] hover:text-[var(--ink)]"}`}
                  data-testid={`switch-reporter-${a.id}`}
                  title={a.tagline}
                >
                  <img src={a.portrait} alt="" className="w-4 h-4 rounded-full" />
                  {a.name}
                </button>
              ))}
              {sessionId && (
                <button onClick={() => { setSessionId(null); setTurns([]); }} className="text-[10.5px] text-[var(--blue)] font-bold hover:underline ml-auto flex items-center gap-1" data-testid="ask-reporter-reset">
                  <RefreshCw size={10} /> New conversation
                </button>
              )}
            </div>
          )}
        </div>

        {/* Transcript */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-5 py-3 space-y-3" data-testid="ask-reporter-transcript">
          {turns.length === 0 && !busy && (
            <div className="py-2">
              <p className="text-[12.5px] text-[var(--muted)] italic mb-3">
                Ask {activeAvatar?.name || "the Newseze newsroom"} anything about this story. They&apos;ll answer from the published reporting — no speculation, no drama.
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="ask-reporter-starters">
                {STARTERS.map((s) => (
                  <button key={s} type="button" onClick={() => sendQuestion(s)} className="px-2.5 py-1 rounded-full text-[11.5px] bg-[#fff9ed] border border-[var(--gold)] text-[#7a5b00] font-bold hover:bg-[var(--gold)] hover:text-[#1d1303] transition" data-testid={`ask-starter-${s.slice(0, 10)}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {turns.map((t, i) => (
            <div key={i} className="space-y-2" data-testid={`ask-turn-${i}`}>
              <div className="flex justify-end">
                <div className="rounded-2xl rounded-tr-sm bg-[var(--ink)] text-white px-3 py-2 max-w-[80%] text-[13.5px] leading-snug" data-testid={`ask-question-${i}`}>
                  {t.question}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <img src={t.avatar?.portrait} alt={t.avatar?.name} className="w-7 h-7 rounded-full bg-[var(--bg)] shrink-0 mt-0.5" />
                <div className="rounded-2xl rounded-tl-sm bg-[var(--bg)] text-[var(--ink)] px-3 py-2 max-w-[85%] text-[13.5px] leading-snug" data-testid={`ask-answer-${i}`}>
                  <div className="text-[9.5px] uppercase tracking-[0.16em] font-black text-[var(--gold)] mb-0.5">{t.avatar?.name}</div>
                  {t.answer}
                  <div className="mt-1.5">
                    <button onClick={() => playAnswer(i)} disabled={loadingAudio === i} className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] font-black text-[var(--blue)] hover:text-[var(--ink)] disabled:opacity-50" data-testid={`ask-listen-${i}`}>
                      {loadingAudio === i ? <Loader2 size={11} className="animate-spin" /> : audioByTurn[i] ? <Pause size={11} /> : <Volume2 size={11} />}
                      {audioByTurn[i] ? "Replay" : "Listen"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {busy && (
            <div className="flex items-center gap-2 text-[12.5px] text-[var(--muted)] italic py-2" data-testid="ask-reporter-thinking">
              <Loader2 size={13} className="animate-spin" /> {activeAvatar?.name || "Reporter"} is reviewing the story…
            </div>
          )}

          {err && (
            <div className="rounded-md bg-rose-50 border border-rose-200 p-2.5 text-[12px] text-rose-800 flex items-start gap-2" data-testid="ask-reporter-error">
              <AlertCircle size={13} className="shrink-0 mt-0.5" />
              <div className="flex-1">
                {err}
                {/upgrade|limit reached/i.test(err) && onUpgrade && (
                  <button onClick={onUpgrade} className="block mt-1.5 text-[var(--blue)] font-black underline" data-testid="ask-reporter-upgrade-cta">
                    See plans →
                  </button>
                )}
              </div>
            </div>
          )}
          <div ref={transcriptEndRef} />
        </div>

        {/* Composer */}
        <div className="shrink-0 border-t border-[var(--line)] p-3 sm:p-4 bg-white">
          {quota && (
            <div className="text-[10.5px] text-[var(--muted)] mb-1.5" data-testid="ask-reporter-quota">
              Questions today: <b className="text-[var(--ink)]">{quota.used}/{quota.cap === 9999 ? "∞" : quota.cap}</b> · <span className="uppercase tracking-[0.12em] font-black">{quota.tier}</span>
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); sendQuestion(); }} className="flex items-end gap-2">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={`Ask ${activeAvatar?.name || "the reporter"}…`}
              rows={2}
              maxLength={400}
              className="flex-1 border border-[var(--line)] rounded-lg px-3 py-2 text-[13.5px] resize-none focus:outline-none focus:border-[var(--gold)]"
              disabled={busy}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendQuestion(); } }}
              data-testid="ask-reporter-input"
            />
            <button type="submit" disabled={busy || !question.trim()} className="btn primary shrink-0" data-testid="ask-reporter-send">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              <span className="hidden sm:inline">Ask</span>
            </button>
          </form>
          {isFreeTier && !quota && (
            <div className="text-[10.5px] text-[var(--muted)] mt-1.5 italic">
              Free: 1 question/day · Reader $6.99: 5/day · Pro: 20/day · Channels & Elite: unlimited.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
